import React, { useState, useEffect } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../api/axiosInstance';
import { Role, User } from '../../types/users';
import toast from 'react-hot-toast';
import { generateSecurePassword } from '../../utils/passwordGenerator';
import {
  ID_AGENTE_VENTAS,
  ID_GERENTE_VENTAS,
  ID_LIDER_VENTAS,
  ID_ROL_LISTA_LIDERES_PARA_AGENTE,
} from '../../constants/roleIds';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  roles: Role[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  roles
}) => {
  const sanitizeErrorMessage = (text: string): string =>
    text
      .replace(/\*\*/g, '')
      .replace(/\s*\(ID:\s*\d+\)\s*\.?$/i, '')
      .trim()

  const showClosableToast = (message: string, type: 'success' | 'error') => {
    toast((t) => (
      <div className="flex items-start gap-3">
        <p className="text-sm leading-5 text-gray-800">{message}</p>
        <button
          type="button"
          onClick={() => toast.dismiss(t.id)}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Cerrar notificación"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    ), {
      duration: 5000,
      position: 'top-center',
      icon: type === 'success' ? '✅' : '❌'
    })
  }

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role_id: '',
    id_agente: '',
    agente_id: '',
    lider_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isOfimaUser, setIsOfimaUser] = useState(false);
  const [idAgenteError, setIdAgenteError] = useState<string | null>(null);
  const [supervisoresDisponibles, setSupervisoresDisponibles] = useState<User[]>([]);
  const [loadingSupervisores, setLoadingSupervisores] = useState(false);

  useEffect(() => {
    const rid = formData.role_id
    const esAgente = rid === String(ID_AGENTE_VENTAS)
    const esLider = rid === String(ID_LIDER_VENTAS)

    if (!esAgente && !esLider) {
      setSupervisoresDisponibles([]);
      setFormData(prev => ({ ...prev, agente_id: '', lider_id: '' }));
      return
    }

    setFormData(prev => ({ ...prev, lider_id: '' }))
    const rolListado = esAgente ? ID_ROL_LISTA_LIDERES_PARA_AGENTE : ID_GERENTE_VENTAS
    setLoadingSupervisores(true)
    axiosInstance.get(`/users/list?role_id=${rolListado}&limit=1000`)
      .then(res => {
        if (res.data.success) {
          setSupervisoresDisponibles(res.data.data || []);
        } else {
          setSupervisoresDisponibles([]);
        }
      })
      .catch(() => setSupervisoresDisponibles([]))
      .finally(() => setLoadingSupervisores(false));
  }, [formData.role_id]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, loading, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setIdAgenteError(null);

    try {
      const payload: Record<string, any> = {
        ...formData,
        role_id: Number(formData.role_id),
        agente_id: formData.agente_id ? Number(formData.agente_id) : null,
        lider_id:
          (formData.role_id === String(ID_AGENTE_VENTAS) || formData.role_id === String(ID_LIDER_VENTAS)) &&
          formData.lider_id
            ? Number(formData.lider_id)
            : null
      };

      if (isOfimaUser) {
        payload.id_agente = formData.id_agente.trim();
      } else {
        delete payload.id_agente;
      }

      const response = await axiosInstance.post('/users/register', payload);

      if (response.data.success) {
        showClosableToast('Usuario creado correctamente', 'success');
        onSuccess();
        onClose();
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          role_id: '',
          id_agente: '',
          agente_id: '',
          lider_id: ''
        });
        setShowPassword(false);
        setIsOfimaUser(false);
      } else {
        showClosableToast(response.data.message || 'Error al crear el usuario', 'error');
      }
    } catch (error: any) {
      console.error('Error al crear usuario:', error);
      const backendMessage = error.response?.data?.message || 'Error al crear el usuario';
      const sanitizedMessage = sanitizeErrorMessage(backendMessage);
      if (isOfimaUser && backendMessage.toLowerCase().includes('id de ofima')) {
        setIdAgenteError(sanitizedMessage);
      }
      showClosableToast(sanitizedMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleGeneratePassword = () => {
    const generatedPassword = generateSecurePassword(11, 15);
    setFormData(prev => ({ ...prev, password: generatedPassword }));
    setShowPassword(true);
    showClosableToast('Contraseña segura generada', 'success');
  };

  const parsedIdAgenteError = (() => {
    if (!idAgenteError) return null;
    const regex = /ID de ofima\s+(.+?)\s+ya est[aá]\s+registrado en el usuario\s+(.+?)(?:\s+\(ID:\s*(\d+)\))?$/i;
    const matches = idAgenteError.match(regex);
    if (!matches) return null;
    return {
      ofimaId: matches[1],
      userName: matches[2],
      userId: matches[3]
    };
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Crear Nuevo Usuario</h2>
            <p className="mt-1 text-sm text-gray-500">Completa la información para registrar el usuario</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            disabled={loading}
            aria-label="Cerrar modal de creación de usuario"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                Nombre
              </label>
              <input
                type="text"
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                className="mt-1 block h-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                Apellido
              </label>
              <input
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                className="mt-1 block h-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="mt-1 block h-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Contraseña
            </label>
            <div className="relative mt-1">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="block h-10 w-full rounded-md border-gray-300 pr-20 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
                minLength={8}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                disabled={loading}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="absolute inset-y-0 right-10 flex items-center px-2 text-gray-500 hover:text-blue-600 disabled:opacity-50"
                aria-label="Generar contraseña segura"
                title="Generar contraseña segura"
                disabled={loading}
              >
                <KeyIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">Mínimo 8 caracteres</p>
          </div>

          <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">¿Es usuario OFIMA?</span>
            <button
              type="button"
              role="switch"
              aria-checked={isOfimaUser}
              aria-label="Alternar usuario OFIMA"
              onClick={() => {
                const nextValue = !isOfimaUser;
                setIsOfimaUser(nextValue);
                if (!nextValue) {
                  setFormData(prev => ({ ...prev, id_agente: '' }));
                }
              }}
              disabled={loading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
                isOfimaUser ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  isOfimaUser ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {isOfimaUser && (
            <div>
              <label htmlFor="id_agente" className="block text-sm font-medium text-gray-700">
                ID USUARIO OFIMA
              </label>
              <input
                type="text"
                id="id_agente"
                value={formData.id_agente}
                onChange={(e) => setFormData(prev => ({ ...prev, id_agente: e.target.value }))}
                className="mt-1 block h-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Ej: 01"
                required={isOfimaUser}
                disabled={loading}
              />
              {idAgenteError && (
                <p className="mt-1 text-xs text-red-600">
                  {parsedIdAgenteError ? (
                    <>
                      El ID de OFIMA <strong>{parsedIdAgenteError.ofimaId}</strong> ya está registrado en el usuario{' '}
                      <strong>{parsedIdAgenteError.userName}</strong>.
                    </>
                  ) : (
                    idAgenteError
                  )}
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="role_id" className="block text-sm font-medium text-gray-700">
              Rol
            </label>
            <select
              id="role_id"
              value={formData.role_id}
              onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 h-10"
              required
              disabled={loading}
            >
              <option value="">Seleccione un rol</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          {(formData.role_id === String(ID_AGENTE_VENTAS) || formData.role_id === String(ID_LIDER_VENTAS)) && (
            <div>
              <label htmlFor="lider_id" className="block text-sm font-medium text-gray-700">
                {formData.role_id === String(ID_AGENTE_VENTAS) ? 'Líder de ventas' : 'Gerente de ventas'}
              </label>
              <select
                id="lider_id"
                value={formData.lider_id}
                onChange={e => setFormData(prev => ({ ...prev, lider_id: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 h-10"
                required
                disabled={loadingSupervisores}
              >
                <option value="">
                  {formData.role_id === String(ID_AGENTE_VENTAS)
                    ? 'Seleccione un líder de ventas'
                    : 'Seleccione un gerente de ventas'}
                </option>
                {supervisoresDisponibles.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </option>
                ))}
              </select>
              {loadingSupervisores && (
                <div className="text-xs text-gray-400 mt-1">
                  {formData.role_id === String(ID_AGENTE_VENTAS) ? 'Cargando líderes...' : 'Cargando gerentes...'}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creando...' : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal; 